// Which academic year the teacher console is showing.
//
// The school's current year unless the teacher picked an earlier one from the
// header's switcher. The choice lives in a small module store rather than the
// URL so it survives moving between My papers and Analytics, and in
// sessionStorage so a reload keeps it — but never across tabs or sign-ins,
// where landing in last year by surprise would look like lost work.
//
// The server is the authority on what is writable: a previous year's
// classroom answers 409 to every write whatever this hook says. `readOnly`
// here only stops the screens from offering what would be refused.
import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { TeacherYearsDTO } from '@/types';

const STORAGE_KEY = 'cwy-teacher-year';

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

let picked: string | null = typeof window === 'undefined' ? null : readStored();
const listeners = new Set<() => void>();

function setPicked(yearId: string | null) {
  picked = yearId;
  try {
    if (yearId) window.sessionStorage.setItem(STORAGE_KEY, yearId);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode: the choice simply does not outlive the page.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export interface TeacherYearState {
  /** The switcher's data — current year, earlier years, the rooms they look after. */
  years: TeacherYearsDTO | undefined;
  /** The year on screen; null until the list has loaded. */
  year: { id: string; label: string; current: boolean } | null;
  /** A previous year: open and print, never create or edit. */
  readOnly: boolean;
  /** `?year=…` (with its leading `?`/`&` left to the caller), or '' for the current year. */
  yearParam: string;
  setYear: (yearId: string) => void;
}

export function useTeacherYear(): TeacherYearState {
  const pickedId = useSyncExternalStore(
    subscribe,
    () => picked,
    () => null,
  );
  const yearsQuery = useQuery({
    queryKey: ['teacher-years'],
    queryFn: () => apiGet<TeacherYearsDTO>('/teacher/years'),
    staleTime: 60_000,
  });
  const years = yearsQuery.data;
  // A stored id the list no longer offers (another school, a year since
  // removed) falls back to the current year instead of an empty console.
  const year = years
    ? (years.years.find((y) => y.id === pickedId) ?? years.years.find((y) => y.current) ?? null)
    : null;
  const readOnly = Boolean(year && !year.current);
  return {
    years,
    year,
    readOnly,
    yearParam: readOnly && year ? `year=${encodeURIComponent(year.id)}` : '',
    setYear: (yearId) =>
      setPicked(years?.years.find((y) => y.id === yearId)?.current ? null : yearId),
  };
}

/** `/path` plus the year, joined with `?` or `&` as the path needs. */
export function withYear(path: string, yearParam: string): string {
  if (!yearParam) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${yearParam}`;
}
